import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /** @type {Array<{ el: Element; cleanup: () => void }>} */
  #carouselDragCleanups = [];

  /** @type {Array<() => void>} */
  #carouselArrowCleanups = [];

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
    this.#setupCarouselDragScroll();
    this.#setupCarouselArrows();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    for (const { cleanup } of this.#carouselDragCleanups) {
      cleanup();
    }
    this.#carouselDragCleanups = [];
    for (const cleanup of this.#carouselArrowCleanups) {
      cleanup();
    }
    this.#carouselArrowCleanups = [];
  }

  /**
   * Enables drag-to-scroll on variant carousels for desktop.
   */
  #setupCarouselDragScroll() {
    for (const { cleanup } of this.#carouselDragCleanups) {
      cleanup();
    }
    this.#carouselDragCleanups = [];

    const carousels = this.querySelectorAll('.variant-option__carousel');
    for (const el of carousels) {
      if (!(el instanceof HTMLElement)) continue;
      let isDown = false;
      let startX = 0;
      let scrollLeft = 0;

      const onMouseDown = (e) => {
        if (e.button !== 0) return;
        isDown = true;
        el.style.cursor = 'grabbing';
        el.style.userSelect = 'none';
        startX = e.pageX;
        scrollLeft = el.scrollLeft;
      };

      const onMouseLeave = () => {
        isDown = false;
        el.style.cursor = '';
        el.style.userSelect = '';
      };

      const onMouseUp = () => {
        isDown = false;
        el.style.cursor = '';
        el.style.userSelect = '';
      };

      const onMouseMove = (e) => {
        if (!isDown) return;
        e.preventDefault();
        const walk = e.pageX - startX;
        el.scrollLeft = scrollLeft - walk;
      };

      el.style.cursor = 'grab';
      el.addEventListener('mousedown', onMouseDown);
      el.addEventListener('mouseleave', onMouseLeave);
      el.addEventListener('mouseup', onMouseUp);
      el.addEventListener('mousemove', onMouseMove);

      this.#carouselDragCleanups.push(() => {
        el.style.cursor = '';
        el.style.userSelect = '';
        el.removeEventListener('mousedown', onMouseDown);
        el.removeEventListener('mouseleave', onMouseLeave);
        el.removeEventListener('mouseup', onMouseUp);
        el.removeEventListener('mousemove', onMouseMove);
      });
    }
  }

  /**
   * Sets up prev/next arrow buttons for variant carousels and scroll-by-step behavior.
   */
  #setupCarouselArrows() {
    for (const cleanup of this.#carouselArrowCleanups) {
      cleanup();
    }
    this.#carouselArrowCleanups = [];

    const outers = this.querySelectorAll('.variant-option__carousel-outer');
    for (const outer of outers) {
      const carousel = outer.querySelector('.variant-option__carousel');
      const arrowsContainer = outer.previousElementSibling;
      const prevBtn = arrowsContainer?.querySelector('.variant-option__carousel-arrow--prev');
      const nextBtn = arrowsContainer?.querySelector('.variant-option__carousel-arrow--next');
      if (!(carousel instanceof HTMLElement) || !prevBtn || !nextBtn) continue;

      const getStep = () => {
        const card = carousel.querySelector('.variant-option__carousel-card');
        const gap = parseFloat(getComputedStyle(carousel).gap) || 12;
        const cardWidth = card ? card.getBoundingClientRect().width : 160;
        return cardWidth + gap;
      };

      const updateDisabled = () => {
        const { scrollLeft, scrollWidth, clientWidth } = carousel;
        const atStart = scrollLeft <= 1;
        const atEnd = scrollLeft >= scrollWidth - clientWidth - 1;
        prevBtn.toggleAttribute('disabled', atStart);
        nextBtn.toggleAttribute('disabled', atEnd);
      };

      const onPrev = () => {
        carousel.scrollBy({ left: -getStep(), behavior: 'smooth' });
      };
      const onNext = () => {
        carousel.scrollBy({ left: getStep(), behavior: 'smooth' });
      };

      prevBtn.addEventListener('click', onPrev);
      nextBtn.addEventListener('click', onNext);
      carousel.addEventListener('scroll', updateDisabled, { passive: true });
      updateDisabled();

      this.#carouselArrowCleanups.push(() => {
        prevBtn.removeEventListener('click', onPrev);
        nextBtn.removeEventListener('click', onNext);
        carousel.removeEventListener('scroll', updateDisabled);
      });
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    const fieldset = event.target.closest('fieldset');
    const valueSpan = fieldset?.querySelector('.variant-option__selected-value');
    if (valueSpan) valueSpan.textContent = selectedOption.value ?? '';
    const selectedOptionValueId = selectedOption.dataset.optionValueId ?? '';
    const legendBadges = fieldset?.querySelectorAll('.variant-option__legend-stock-badge[data-option-value-id]');
    if (legendBadges?.length) {
      legendBadges.forEach((badge) => {
        if (!(badge instanceof HTMLElement)) return;
        badge.toggleAttribute('hidden', badge.dataset.optionValueId !== selectedOptionValueId);
      });
    }
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    if (window.__SHOPIFY_DEBUG_VARIANTS__) {
      console.debug('[variant-picker] variant:selected', {
        productId: this.dataset.productId,
        optionValueId: selectedOption.dataset.optionValueId,
        variantId: selectedOption.dataset.variantId || null,
        optionAvailable: selectedOption.dataset.optionAvailable,
        selectedOptionsValues: this.selectedOptionsValues,
      });
    }

    const target = event.target;
    queueMicrotask(() => {
      const isOnProductPage =
        this.dataset.templateProductMatch === 'true' &&
        !target.closest('product-card') &&
        !target.closest('quick-add-dialog');

      const currentUrl = this.dataset.productUrl?.split('?')[0];
      const newUrl = selectedOption.dataset.connectedProductUrl;
      const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
      const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

      const morphElementSelector = loadsNewProduct
        ? 'main'
        : isOnFeaturedProductSection
        ? 'featured-product-information'
        : undefined;

      this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

      const url = new URL(window.location.href);

      const variantId = selectedOption.dataset.variantId || null;

      if (isOnProductPage) {
        if (variantId) {
          url.searchParams.set('variant', variantId);
        } else {
          url.searchParams.delete('variant');
        }
      }

      if (loadsNewProduct) {
        url.pathname = newUrl;
      }

      if (url.href !== window.location.href) {
        yieldToMainThread().then(() => {
          history.replaceState({}, '', url.toString());
        });
      }
    });
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    requestAnimationFrame(() => {
      const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
      if (measurements) {
        this.#applyFieldsetMeasurements(measurements);
      }
    });
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    // On product page with option_values: request section so Shopify resolves the correct variant
    // (Section Rendering API applies option_values to the product in that section).
    if (this.dataset.sectionId && params.some((p) => p.startsWith('option_values='))) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${this.dataset.sectionId}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const matchingProductId = this.dataset.productId || '';
        const escapedProductId =
          typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(matchingProductId) : String(matchingProductId);
        const matchingVariantPicker =
          (matchingProductId
            ? html.querySelector(`variant-picker[data-product-id="${escapedProductId}"]`)
            : null) || html.querySelector('variant-picker');

        const textContent = matchingVariantPicker?.querySelector('script[type="application/json"]')?.textContent;
        if (!textContent) return;

        const variantResource = JSON.parse(textContent);

        if (window.__SHOPIFY_DEBUG_VARIANTS__) {
          console.debug('[variant-picker] variant:update payload', {
            productId: this.dataset.productId,
            variantId: variantResource?.id,
            available: variantResource?.available,
            requestUrl,
            morphElementSelector: morphElementSelector || 'picker',
          });
        }

        if (morphElementSelector === 'main') {
          this.updateMain(html);
          this.#dispatchVariantUpdateAfterMorph(document.querySelector('main'), variantResource, html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
          const container = document.querySelector(morphElementSelector);
          this.#dispatchVariantUpdateAfterMorph(container, variantResource, html);
        } else {
          // We grab the variant object from the response and dispatch an event with it
          // so the product form can enable add-to-cart and update variant id.
          let sourceId = '';
          try {
            sourceId = this.selectedOptionId ?? '';
          } catch {
            // selectedOptionId can throw if no option value id; product form only needs resource
          }

          // Dispatch BEFORE morphing picker to avoid losing event timing when the element is replaced.
          if (variantResource != null) {
            const preMorphEventData = {
              html,
              productId: this.dataset.productId ?? '',
              newProduct: undefined,
            };
            this.dispatchEvent(new VariantUpdateEvent(variantResource, sourceId, preMorphEventData));
            document.dispatchEvent(new VariantUpdateEvent(variantResource, sourceId, preMorphEventData));
            this.#syncProductFormFallback(variantResource);
          }

          const newProduct = this.updateVariantPicker(html);

          // Dispatch once more after morph in case combined-listing changed product context.
          if (variantResource != null) {
            const postMorphEventData = {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            };
            this.dispatchEvent(new VariantUpdateEvent(variantResource, sourceId, postMorphEventData));
            document.dispatchEvent(new VariantUpdateEvent(variantResource, sourceId, postMorphEventData));
            this.#syncProductFormFallback(variantResource);
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Dispatches VariantUpdateEvent from the variant picker inside the morphed container
   * so the product form (e.g. add-to-cart) can enable/update after a full-section morph.
   * @param {Element | null} container - The morphed container (e.g. main or featured-product-information).
   * @param {Object} variantResource - Parsed variant JSON from the section response.
   * @param {Document} html - The response document.
   */
  #dispatchVariantUpdateAfterMorph(container, variantResource, html) {
    const matchingProductId = this.dataset.productId || '';
    const escapedProductId =
      typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(matchingProductId) : String(matchingProductId);
    const variantPicker =
      (matchingProductId
        ? container?.querySelector?.(`variant-picker[data-product-id="${escapedProductId}"]`)
        : null) || container?.querySelector?.('variant-picker');
    if (!variantPicker) return;
    let sourceId = '';
    try {
      sourceId = variantPicker.selectedOptionId ?? '';
    } catch {
      // selectedOptionId can throw if no option value id; product form only needs resource and productId
    }
    const variantUpdateEventData = {
      html,
      productId: variantPicker.dataset.productId ?? '',
      newProduct: undefined,
    };

    variantPicker.dispatchEvent(new VariantUpdateEvent(variantResource, sourceId, variantUpdateEventData));
    document.dispatchEvent(new VariantUpdateEvent(variantResource, sourceId, variantUpdateEventData));

    // Fallback sync for morphed containers where bubbling listeners may be re-bound late.
    this.#syncProductFormFallback(variantResource, container || document);
  }

  /**
   * Fallback: directly synchronize matching product form variant id + ATC disabled state.
   * @param {any} variantResource
   * @param {ParentNode} [scope]
   */
  #syncProductFormFallback(variantResource, scope = document) {
    const productId = this.dataset.productId || '';
    if (!productId) return;

    const escapedProductId =
      typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(productId) : String(productId);

    const productForm = scope.querySelector(`product-form-component[data-product-id="${escapedProductId}"]`);
    if (!productForm) return;

    const variantIdInput = productForm.querySelector('input[name="id"]');
    if (variantIdInput) {
      variantIdInput.value = variantResource?.id ?? '';
    }

    const addToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !(variantResource && variantResource.available);
    }

    if (window.__SHOPIFY_DEBUG_VARIANTS__) {
      console.debug('[variant-picker] fallback form sync', {
        productId,
        variantId: variantResource?.id ?? null,
        available: variantResource?.available ?? null,
        buttonDisabled: addToCartButton instanceof HTMLButtonElement ? addToCartButton.disabled : null,
      });
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const matchingProductId = this.dataset.productId || '';
    const escapedProductId =
      typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(matchingProductId) : String(matchingProductId);

    const newVariantPickerSource =
      (matchingProductId
        ? newHtml.querySelector(`${this.tagName.toLowerCase()}[data-product-id="${escapedProductId}"]`)
        : null) || newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();
    this.#setupCarouselDragScroll();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
